import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useFilters, Panel, Loading, Empty, PageHeader, ExportBar, exportExcel, HeatCell, BandDot, ScoreBadge, Delta } from "./_shared";

function StatPill({ label, value }) {
  return (
    <div className="bg-stone-50 rounded-xl px-4 py-3 border border-stone-100">
      <div className="text-[11px] uppercase tracking-wide text-stone-500">{label}</div>
      <div className="font-display text-xl font-semibold text-stone-900 mt-0.5 tabular-nums">{value}</div>
    </div>
  );
}

export default function BatchAnalytics() {
  const { query } = useFilters();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/analytics/batches${query}`).then((r) => setData(r.data.batches)).finally(() => setLoading(false));
  }, [query]);

  if (loading) return <Loading />;
  const batches = (data || []).filter((b) => b.enrolled > 0);

  const handleExcel = () =>
    exportExcel([{
      name: "Batch Summary",
      rows: batches.map((b) => ({
        Batch: b.name, Program: b.program_name, Facilitator: b.facilitator_name,
        Enrolled: b.enrolled, Completed: b.completed, "Completion %": b.completion_pct,
        "Avg Score": b.avg_score, "Avg Attempts": b.avg_attempts, "Avg Time (min)": b.avg_time_min,
      })),
    }], "batch-analytics.xlsx");

  return (
    <div data-testid="batch-analytics-page">
      <PageHeader title="Batch Analytics"
        description="Summary, competency heatmap and movement for every training batch in scope."
        right={batches.length > 0 && <ExportBar onExcel={handleExcel} />} />

      {batches.length === 0 ? <Empty label="No batches with completed sessions in scope." /> : (
        <div className="space-y-6">
          {batches.map((b) => (
            <Panel key={b.batch_id} testid={`batch-card-${b.batch_id}`}
              title={b.name} subtitle={`${b.program_name} • ${b.facilitator_name}`}
              right={<ScoreBadge value={b.avg_score} />}>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
                <StatPill label="Enrolled" value={b.enrolled} />
                <StatPill label="Completed" value={b.completed} />
                <StatPill label="Completion" value={`${b.completion_pct}%`} />
                <StatPill label="Avg Score" value={b.avg_score} />
                <StatPill label="Avg Attempts" value={b.avg_attempts} />
                <StatPill label="Avg Time" value={`${b.avg_time_min}m`} />
              </div>

              <div className="text-[11px] uppercase tracking-wide text-stone-500 mb-2">Competency Heatmap</div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mb-6">
                {b.competency_heatmap.map((c) => (
                  <div key={c.competency}>
                    <div className="text-xs text-stone-600 mb-1 truncate" title={c.competency}>{c.competency}</div>
                    <HeatCell value={c.score} />
                  </div>
                ))}
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-emerald-50/50 rounded-xl p-4 border border-emerald-100">
                  <div className="text-xs font-semibold text-emerald-800 mb-2">Highest Performing</div>
                  {b.highest_competencies.map((c) => (
                    <div key={c.competency} className="flex justify-between text-sm py-0.5">
                      <span className="text-stone-700 flex items-center gap-2"><BandDot value={c.score} />{c.competency}</span>
                      <span className="font-semibold tabular-nums">{c.score}</span>
                    </div>
                  ))}
                </div>
                <div className="bg-red-50/40 rounded-xl p-4 border border-red-100">
                  <div className="text-xs font-semibold text-[#b81f24] mb-2">Lowest Performing</div>
                  {b.lowest_competencies.map((c) => (
                    <div key={c.competency} className="flex justify-between text-sm py-0.5">
                      <span className="text-stone-700 flex items-center gap-2"><BandDot value={c.score} />{c.competency}</span>
                      <span className="font-semibold tabular-nums">{c.score}</span>
                    </div>
                  ))}
                </div>
                <div className="bg-stone-50 rounded-xl p-4 border border-stone-100">
                  <div className="text-xs font-semibold text-stone-700 mb-2">Most Improved</div>
                  {b.most_improved.length === 0 && <div className="text-xs text-stone-400">Needs repeat attempts</div>}
                  {b.most_improved.map((c) => (
                    <div key={c.competency} className="flex justify-between text-sm py-0.5">
                      <span className="text-stone-700">{c.competency}</span>
                      <Delta value={c.delta} />
                    </div>
                  ))}
                </div>
              </div>
            </Panel>
          ))}
        </div>
      )}
    </div>
  );
}
